import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { useMount } from 'react-use'
import styled, { css } from 'styled-components'
import { match, P } from 'ts-pattern'

import { Button } from '@blade/ui-kit'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { walletsByIdAtom } from '@root/entities/wallet/atoms'
import { goBackAction } from '@root/features/routing/atoms'
import {
  ConfirmTransferStep,
  InputTransferStep,
  reatomTransferFlow,
  TransferAmount,
  TransferCard,
  TransferFeeCardEvm,
  TransferFeeCardSvm,
  TransferReviewDrawer,
} from '@root/features/wallet/transfer'
import { Screen, SettingsHeader } from '@root/shared/layout'
import { Keyboard } from '@root/shared/ui-kit'

import type { WalletModel } from '@root/entities/wallet/atoms'
import type { WalletId } from '@root/entities/wallet/model'

export const TransferScreen: React.FC = () => {
  const { walletId } = useParams<{ walletId: WalletId }>()

  const [wallet] = useAtom((ctx) => {
    if (!walletId) {
      return undefined
    }
    const walletsById = ctx.spy(walletsByIdAtom)
    return walletsById.get(walletId)
  })

  if (!wallet) {
    // TODO: Handle loading and errors
    return
  }

  return <TransferScreen_ wallet={wallet} />
}

const TransferScreen_: React.FC<{ wallet: WalletModel }> = ({ wallet }) => {
  const { t } = useTranslation()
  const goBack = useAction(goBackAction)

  const model = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomTransferFlow({ wallet: wallet }),
    [wallet],
  )

  const [network] = useAtom(selectedNetworkAtom)
  const [amountRaw] = useAtom(model.amount.amountRawAtom)
  const [validationError] = useAtom(model.validationAtom)
  const [step, setStep] = useAtom(model.stepAtom)

  const setAmount = useAction(model.amount.setAmountAction)
  const initialize = useAction(model.initializeAction)

  useMount(() => {
    initialize()
  })

  const renderFeeCard = () =>
    match(network)
      .with(Network.Solana, () => (
        <>
          <TransferFeeCardSvm model={model.svmFeeModel} />
          <TransferFeeCardEvm model={model.gasSettings} />
        </>
      ))
      .with(P.union(Network.Ethereum, Network.Base, Network.Bsc), () => (
        <TransferFeeCardEvm model={model.gasSettings} />
      ))
      .with(Network.Ton, () => <></>)
      .exhaustive()

  return (
    <Root>
      <SettingsHeader title={t('transfer')} onBack={goBack} />
      <Content>
        <TransferCard wallet={wallet} model={model} />
        <TransferAmount
          amountModel={model.amount}
          currencyModel={model.currency}
          validation={validationError}
        />
        {renderFeeCard()}
        <Keyboard
          css={css`
            align-self: end;
            margin-block: 16px 24px;
          `}
          value={amountRaw}
          onChange={(value) => setAmount(value)}
        />
        <ActionContainer>
          <Button
            onClick={goBack}
            size="m"
            variant="secondary"
            radius="rounded"
          >
            {t('common.cancel')}
          </Button>
          <Button
            size="m"
            color="primary"
            radius="rounded"
            onClick={() => {
              setStep(new ConfirmTransferStep())
            }}
            disabled={!!validationError}
          >
            {t('common.next')}
          </Button>
        </ActionContainer>
      </Content>

      <TransferReviewDrawer
        open={step instanceof ConfirmTransferStep}
        onOpenChange={() => {
          setStep(new InputTransferStep())
        }}
        onDone={() => {
          goBack()
        }}
        model={model}
      />
    </Root>
  )
}

const Root = styled(Screen)`
  display: grid;
  grid-template-rows: auto 1fr;
`

const Content = styled.div`
  display: grid;
  grid-template-rows: auto 1fr auto auto;
  padding-inline: 12px;
  padding-block: 8px 34px;
`

const ActionContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  padding-inline: 12px;
`
